/* eslint-disable */
import React, { useState, useCallback } from "react";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";

// --------------- Styled Components ---------------

const ForecastWrapper = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  "& .widget-header": {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
  },
  "& .widget-title": {
    fontWeight: 600,
    fontSize: "1.125rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const PlaceholderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(6),
  textAlign: "center",
  color: theme.palette.text.secondary,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
}));

const TrendCell = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
}));

// --------------- Helpers ---------------

const getTrendIcon = trend => {
  const t = (trend || "").toLowerCase();
  if (t === "improving" || t === "up") {
    return <ArrowUpwardIcon sx={{ color: "success.main", fontSize: 18 }} />;
  }
  if (t === "declining" || t === "down") {
    return <ArrowDownwardIcon sx={{ color: "error.main", fontSize: 18 }} />;
  }
  return <RemoveIcon sx={{ color: "text.disabled", fontSize: 18 }} />;
};

const getTrendColor = trend => {
  const t = (trend || "").toLowerCase();
  if (t === "improving" || t === "up") return "success.main";
  if (t === "declining" || t === "down") return "error.main";
  return "text.secondary";
};

// --------------- Component ---------------

const AiForecastWidget = ({ projectId }) => {
  const [loading, setLoading] = useState(false);
  const [forecasts, setForecasts] = useState([]);
  const [summary, setSummary] = useState("");

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("http://localhost:8000/api/traffic-forecast/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: projectId,
          ranking_history: [],
        }),
      });

      if (!response.ok) {
        throw new Error(`Forecast API returned ${response.status}`);
      }

      const data = await response.json();
      setForecasts(data.forecasts || data.results || []);
      setSummary(data.summary || "");
      toast.success("Traffic forecast generated");
    } catch (err) {
      toast.error(err.message || "Failed to fetch traffic forecast");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const hasData = forecasts.length > 0;

  return (
    <ForecastWrapper>
      <CardContent>
        {/* Header */}
        <Box className="widget-header">
          <Typography className="widget-title">
            <TrendingUpIcon color="primary" />
            Keyword Traffic Forecast
          </Typography>
          <Button variant="outlined" size="small" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={fetchForecast} disabled={loading}>
            {loading ? "Loading..." : "Refresh Forecast"}
          </Button>
        </Box>

        {/* Summary */}
        {summary && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {summary}
          </Typography>
        )}

        {/* Loading */}
        {loading && !hasData && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Placeholder */}
        {!loading && !hasData && (
          <PlaceholderBox>
            <TrendingUpIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
            <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
              No Forecast Data
            </Typography>
            <Typography variant="body2">Click "Refresh Forecast" to generate keyword traffic predictions using AI.</Typography>
          </PlaceholderBox>
        )}

        {/* Data Table */}
        {hasData && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Keyword</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Current Rank
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Predicted (30d)
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Trend
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Slope
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">
                    Days to Page 1
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forecasts.map((row, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {row.keyword}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{row.currentRank ?? "N/A"}</TableCell>
                    <TableCell align="center">{row.predictedRank30d ?? "N/A"}</TableCell>
                    <TableCell align="center">
                      <TrendCell sx={{ justifyContent: "center" }}>
                        {getTrendIcon(row.trend)}
                        <Typography variant="caption" sx={{ color: getTrendColor(row.trend) }}>
                          {row.trend || "stable"}
                        </Typography>
                      </TrendCell>
                    </TableCell>
                    <TableCell align="center">{row.slope != null ? row.slope.toFixed(2) : "N/A"}</TableCell>
                    <TableCell align="center">
                      {row.daysToPageOne != null ? (
                        <Chip
                          label={row.daysToPageOne === -1 ? "N/A" : `${row.daysToPageOne}d`}
                          size="small"
                          color={row.daysToPageOne !== -1 && row.daysToPageOne <= 30 ? "success" : "default"}
                          variant="outlined"
                        />
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </ForecastWrapper>
  );
};

export default AiForecastWidget;
